/**
 * Internal types for the device domain.
 *
 * These types are NOT re-exported from the protocol package — they are
 * implementation details of the IdentityStore + Enroller. Public data
 * models (the bridge wire shape) live in `@serviceme/devtools-protocol`'s
 * `device.ts`.
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `device/types.ts`
 */

import type { DeviceBindingState } from "@serviceme/devtools-protocol";

/**
 * Schema version of the on-disk `device.json` file. Bumped when the
 * shape changes incompatibly. IdentityStore checks this on read and
 * either migrates (versions ≤ 1) or refuses (versions > supported).
 */
export const DEVICE_JSON_SCHEMA_VERSION = 1;

/** Internal representation of the persisted identity file. */
export interface PersistedDeviceIdentity {
	version: number;
	/** Stable per-machine id (UUID v4 shape) — survives secret rotates. */
	installationId: string;
	/** Raw `os.hostname()` for diagnostics. */
	machineId: string;
	/** Platform string (e.g. "darwin"). */
	platform: string;
	/** Optional hostname override for environments where `os.hostname()` is unstable. */
	hostname?: string;
	/** Public, non-secret id returned by the server. 32-char hex. */
	publicId: string;
	/** Monotonic secret version counter, starts at 1 after first enroll. */
	secretVersion: number;
	/** Current binding state — drives the re-enroll matrix. */
	bindingState: DeviceBindingState;
	/** HMAC secret (32 bytes hex-encoded = 64 chars). Persisted per `device-header-spec.md` §3.1. */
	deviceSecret: string;
	/** Optional: previous secret retained during the grace window (rotation). */
	previousDeviceSecret?: string;
	/** Optional: ISO timestamp at which the previous secret stops being accepted. */
	previousSecretExpiresAt?: string;
	/** ISO timestamp of the most recent successful enroll / rotate. */
	lastEnrollAt: string;
	/** Optional ISO timestamp of the most recent server sync. */
	lastSyncAt?: string;
	/** Optional human-readable message for the last sync error. */
	lastSyncError?: string;
}

/** Result of a single atomic write. */
export interface AtomicWriteResult {
	bytesWritten: number;
	/** Path to the temp file (post-rename it no longer exists; useful for diagnostics). */
	tmpPath: string;
}

/** Hook called before/after every identity write — used by tests to assert concurrency safety. */
export interface IdentityStoreHooks {
	beforeWrite?: (next: PersistedDeviceIdentity) => void | Promise<void>;
	afterWrite?: (next: PersistedDeviceIdentity) => void | Promise<void>;
}
